import { TestCardContainer, TestVerifyResultStyles } from '@Src/_basic/components/MuiStyleComponent/TestComponent';
import ColFieldInfo from '@Src/_basic/components/YatungTest/ColFieldInfo';
import EditButtons from '@Src/_basic/components/YatungTest/EditButtons';
import TestTable from '@Src/_basic/components/YatungTest/TestTable';
import { checkIsNaN } from '@Src/_basic/helpers/CheckIsNaN';
import { formatNumber } from '@Src/_basic/helpers/FormatNumber';
import { basicNumberSchema } from '@Src/_basic/helpers/TestForm';
import { AllTest } from '@Src/_basic/object/TestType';
import {
  ImportSpecificSurface,
  PutSpecificSurfaceRequest,
  SpecificSurface,
} from '@Src/_basic/object/test/cementtitiousPowder/SpecificSurfaceType';
import { TestApi } from '@Src/_basic/protocol/test/TestApi';
import LabelInput from '@Src/app/module/proportionManage/components/LabelInput';
import { Stack } from '@mui/material';
import { useFormik } from 'formik';
import React, { ChangeEvent, memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

interface Props {
  testDetail: SpecificSurface;
  // eslint-disable-next-line no-unused-vars
  afterUpdateTest: (test: AllTest) => void;
  canEdit?: boolean;
}

const validationSchema = Yup.object().shape({
  specificSurface: basicNumberSchema,
});

function ExportSpecificSurfaceTable({ testDetail, afterUpdateTest, canEdit }: Props) {
  const { t: i18T } = useTranslation();
  const [edit, setEdit] = useState<boolean>(false);

  const initialValues = useMemo(
    () => ({
      specificSurface: testDetail?.specificSurface,
    }),
    [testDetail],
  );

  const otherInfo = useMemo(
    () => [
      {
        title: formatNumber(testDetail?.specificSurface?.toFixed(4)),
      },
    ],
    [testDetail?.specificSurface],
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      const data: ImportSpecificSurface = {
        specificSurface: checkIsNaN(+values.specificSurface),
      };

      TestApi.putTest<PutSpecificSurfaceRequest>(
        {
          exptItemCode: testDetail.exptItemCode,
          fields: {
            ...testDetail,
            ...data,
          },
        },
        (_data) => {
          afterUpdateTest(_data);
          setEdit(false);
        },
        (err) => console.log(err),
      );
    },
  });

  const specificSurfaceView = useMemo(() => {
    switch (edit) {
      case true:
        return (
          <ColFieldInfo
            titleStyle={{ ...TestVerifyResultStyles.resultTitle, borderBottom: '1px solid #ccc', paddingBottom: 20 }}
            title={`${i18T(`TEST.SPECIFIC_SURFACE.SPECIFIC_SURFACE`)}(cm²/g)`}
          >
            <LabelInput
              valueWidth="150px"
              value={formik.values.specificSurface}
              isError={!!(formik.touched.specificSurface && formik.errors.specificSurface)}
              onChange={(e: ChangeEvent<HTMLInputElement>) => formik.setFieldValue('specificSurface', e.target.value)}
            />
          </ColFieldInfo>
        );
      case false:
        return (
          <TestTable
            headerData={{ values: [`${i18T(`TEST.SPECIFIC_SURFACE.SPECIFIC_SURFACE`)}(cm²/g)`] }}
            rows={otherInfo}
          />
        );
      default:
        break;
    }
  }, [edit, formik, otherInfo, i18T]);

  return (
    <Stack gap={2}>
      <TestCardContainer>
        <EditButtons edit={edit} setEdit={setEdit} onSubmit={formik.handleSubmit} canEdit={canEdit} />
        {specificSurfaceView}
      </TestCardContainer>
    </Stack>
  );
}

export default memo(ExportSpecificSurfaceTable);
